import React from 'react';
import { Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface PrivateRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

/**
 * PrivateRoute component protects routes that require authentication
 * - Redirects to /login if user is not authenticated
 * - Shows loading spinner while checking authentication status
 * - Optionally requires admin role (admin or superadmin)
 */
export const PrivateRoute: React.FC<PrivateRouteProps> = ({
  children,
  requireAdmin = false
}) => {
  const { user, loading } = useAuth();

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-wine-white">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-wine-purple mx-auto mb-4" />
          <p className="text-gray-600">Caricamento...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check admin requirement
  if (requireAdmin && user.role !== 'admin' && user.role !== 'superadmin') {
    return <Navigate to="/" replace />;
  }

  // User is authenticated (and admin if required)
  return <>{children}</>;
};
